// Imports
import { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components/macro';
import { APIFormOptions } from '..';
import { fieldContainerBottomMargin } from '../../../../../foundation/front-end/components/forms/field-container';
import commonActions from '../../../../../foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../../foundation/front-end/redux/hooks';
import thirdPartyIdentifiers from '../../../../../third-party-identifiers';
import ExpectedFormError from '../../../../../utils/errors/expected-form-error';


// Types
type RecaptchaOptions = NonNullable<APIFormOptions<unknown, unknown, unknown>['recaptcha']> & {
	appearsOnBackground?: boolean;
};


// Styled components
const VisibleChallenge = styled.div<{ visibleChallengeActive: boolean }>`
	> div {
		margin-bottom: ${fieldContainerBottomMargin};
	}
	
	${({ visibleChallengeActive }) =>
		!visibleChallengeActive
			? css`
					display: none;
				`
			: ''}
`;

const RecaptchaLegalNotice = styled.div<Pick<RecaptchaOptions, 'appearsOnBackground'>>`
	text-align: center;
	margin-top: 0.75rem;
	font-size: 0.72rem;
	font-style: normal;
	font-weight: 300;
	line-height: 1;
	color: hsl(0, 0%, 21%);
	
	> a {
		cursor: pointer;
		text-decoration: underline;
		color: inherit;
		
		&:hover {
			text-decoration: none;
		}
	}
	
	${({ appearsOnBackground }) =>
		appearsOnBackground
			? css`
					color: #aaa;
					
					> a {
						color: #656565;
						text-decoration: none;
						
						&:hover {
							text-decoration: underline;
						}
					}
				`
			: ''}
`;


// Define the accepted options
interface Options extends RecaptchaOptions {
	/** Unique ID to be used with reCAPTCHA checkbox */
	id: string;
	
	
	/** State setter for form loading state */
	setIsLoading: (value: React.SetStateAction<boolean>) => void;
}


// A hook that provides reCAPTCHA behavior, UI, and management
const useRecaptcha = ({ enabled, id, elementID, legalNotice, appearsOnBackground, setIsLoading }: Options) => {
	// Use state
	const [visibleChallengeActive, setVisibleChallengeActive] = useState(false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Ref that holds the ID of the last reCAPTCHA visible checkbox initialized by this form
	const visibleCheckboxID = useRef<number>();
	
	
	// Determine reCAPTCHA invisible site key
	const recaptchaSiteKeyInvisible =
		thirdPartyIdentifiers.recaptchaSiteKeys.invisible?.[
			process.env.REACT_APP__ENVIRONMENT === 'production' ? 'prd' : 'dev'
		];
	
	
	// Load reCAPTCHA if enabled and not in Storybook
	useEffect(() => {
		if (!enabled) {
			return;
		}
		
		if ((window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			return;
		}
		
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKeyInvisible}`;
		
		script.async = true;
		document.head.appendChild(script);
		
		return () => {
			document.head.removeChild(script);
		};
	}, [recaptchaSiteKeyInvisible, enabled]);
	
	
	// Get reCAPTCHA headers
	const getRecaptchaHeaders = async () => {
		// If reCAPTCHA is not enabled, do no work
		if (!enabled) {
			return;
		}
		
		
		// Verify reCAPTCHA is available
		if (typeof window.grecaptcha !== 'object') {
			// Show page message
			dispatch(
				commonActions.showPageMessage({
					color: 'danger',
					title: 'Oh snap!',
					message: 'reCAPTCHA hasn’t loaded yet.',
				})
			);
			
			
			// Stop processing
			throw new ExpectedFormError();
		}
		
		
		// Call reCAPTCHA
		if (visibleChallengeActive) {
			return {
				'Recaptcha-Token': window.grecaptcha.enterprise.getResponse(),
				'Recaptcha-Version': 'visible',
			};
		}
		
		return {
			'Recaptcha-Token': await window.grecaptcha.enterprise.execute(recaptchaSiteKeyInvisible, {
				action: 'submit',
			}),
			'Recaptcha-Version': 'invisible',
		};
	};
	
	
	// Keep track of component's mounted state
	const isMounted = useRef(true);
	
	useEffect(() => {
		isMounted.current = true;
		
		return () => {
			isMounted.current = false;
		};
	}, []);
	
	
	// Initialize checkbox container ID
	const checkboxContainerID = `${id}-recaptcha-visible-challenge`;
	
	
	// Render the reCAPTCHA visible challenge
	const renderVisibleRecaptcha = () => {
		if (!visibleChallengeActive) {
			if (visibleCheckboxID.current === undefined) {
				const recaptchaSiteKeyVisible =
					thirdPartyIdentifiers.recaptchaSiteKeys.visible?.[
						process.env.REACT_APP__ENVIRONMENT === 'production' ? 'prd' : 'dev'
					];
				
				visibleCheckboxID.current = grecaptcha.enterprise.render(elementID ?? checkboxContainerID, {
					sitekey: recaptchaSiteKeyVisible,
				});
			}
			
			setVisibleChallengeActive(true);
		}
		
		if (isMounted.current) {
			setIsLoading(false);
		}
	};
	
	
	// Helper function that deactivates the reCAPTCHA challenge
	const deactivateRecaptchaChallenge = () => {
		if (visibleChallengeActive) {
			setVisibleChallengeActive(false);
		}
	};
	
	
	// Helper function that resets the reCAPTCHA checkbox
	const resetRecaptchaCheckbox = () => {
		if (visibleChallengeActive) {
			window.grecaptcha.enterprise.reset(visibleCheckboxID.current);
		}
	};
	
	
	// Build challenge JSX
	const visibleChallengeJSX = (
		<VisibleChallenge id={checkboxContainerID} visibleChallengeActive={visibleChallengeActive} />
	);
	
	
	// Build reCAPTCHA legal notice JSX
	let recaptchaLegalNoticeJSX: JSX.Element | null = null;
	
	if (enabled && !legalNotice?.disabled) {
		recaptchaLegalNoticeJSX = (
			<RecaptchaLegalNotice appearsOnBackground={appearsOnBackground ?? true}>
				This site is protected by reCAPTCHA.
			</RecaptchaLegalNotice>
		);
	}
	
	return {
		visibleChallengeActive,
		getRecaptchaHeaders,
		renderVisibleRecaptcha,
		resetRecaptchaCheckbox,
		deactivateRecaptchaChallenge,
		visibleChallengeJSX,
		recaptchaLegalNoticeJSX,
	};
};

export default useRecaptcha;
